/**
 * Progress Component
 *
 * `intent` colours the indicator with a semantic status colour (a tenant theme
 * sets these too). It routes through the indicator's own className — never an
 * arbitrary `[&>div]:bg-…` selector on Radix's internal DOM.
 */

import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cn } from '@tonnie/foundation';

import type { ProgressName } from './control-name';

export type ProgressIntent = 'default' | 'success' | 'info' | 'warning';

const INTENT_INDICATOR: Record<ProgressIntent, string> = {
  default: 'bg-primary',
  success: 'bg-success',
  info: 'bg-info',
  warning: 'bg-warning',
};

/** A bar shows no words of its own, so it must be named (see ProgressName). */
export type ProgressProps = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
  intent?: ProgressIntent;
} & ProgressName;

export const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, intent = 'default', ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    // Forwarded, not only read: Radix derives `aria-valuenow` and `data-state`
    // from it. Without it every bar announced itself as indeterminate.
    value={value}
    className={cn('relative h-2 w-full overflow-hidden rounded-full bg-muted', className)}
    {...props}
  >
    <ProgressPrimitive.Indicator
      data-intent={intent}
      className={cn('h-full w-full flex-1 transition-all', INTENT_INDICATOR[intent])}
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    />
  </ProgressPrimitive.Root>
));

Progress.displayName = ProgressPrimitive.Root.displayName;
